import { type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  BuildingsIcon,
  ChartLineUpIcon,
  CurrencyRubIcon,
  BrainIcon,
  PulseIcon,
  SignOutIcon,
  ShieldCheckIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import { cn } from '@smena/ui';
import { useLogout, useMe } from '@/features/auth/hooks';

const NAV = [
  { to: '/overview', label: 'Обзор', icon: <ChartLineUpIcon weight="bold" /> },
  { to: '/accounts', label: 'Аккаунты', icon: <BuildingsIcon weight="bold" /> },
  { to: '/payments', label: 'Платежи', icon: <CurrencyRubIcon weight="bold" /> },
  { to: '/audit', label: 'Аудит', icon: <ShieldCheckIcon weight="bold" /> },
  { to: '/admins', label: 'Администраторы', icon: <UsersThreeIcon weight="bold" /> },
  { to: '/llm', label: 'LLM', icon: <BrainIcon weight="bold" /> },
  { to: '/system', label: 'Система', icon: <PulseIcon weight="bold" /> },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { data: user } = useMe();
  const logoutMut = useLogout();
  const navigate = useNavigate();

  async function handleLogout() {
    await logoutMut.mutateAsync();
    navigate('/login', { replace: true });
  }

  return (
    <div className="grid h-dvh grid-cols-[220px_1fr] bg-bg text-text">
      {/* Рельс тёмный, как в панели УК, но акцент фиолетовый - плоскости не путаются (ADR-18). */}
      <aside className="flex flex-col bg-ink px-3 py-3.5 text-on-ink">
        <div className="flex h-8 items-center gap-2.5 px-0.5 text-base font-bold tracking-tight">
          <i className="size-2.5 rotate-45 bg-accent shadow-[0_0_0_4px_rgba(109,40,217,.24)]" />
          <span>Smena</span>
          <span className="rounded-sm bg-accent px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            admin
          </span>
        </div>
        <nav className="mt-3.5 flex flex-col gap-0.5" aria-label="Разделы админки">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-semibold transition-colors',
                  isActive
                    ? 'bg-accent text-white'
                    : 'text-muted-on-ink hover:bg-white/8 hover:text-white',
                )
              }
            >
              <span className="grid size-5 place-items-center text-[18px]">{item.icon}</span>
              <span className="flex-1">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto border-t border-white/10 px-2 pt-3 text-xs text-muted-on-ink">
          Версия 0.2.0 · ADR-18
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-line bg-panel px-5">
          {/* Постоянное напоминание: это плоскость вендора, тут видны чужие ПДн и всё пишется в журнал. */}
          <div className="text-sm text-muted">
            Внутренняя панель вендора · действия пишутся в <code className="text-xs">admin_audit</code>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted">{user?.email}</span>
            <button
              type="button"
              onClick={handleLogout}
              disabled={logoutMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-line-control px-3 py-1.5 text-xs hover:bg-panel2 disabled:opacity-60"
              aria-label="Выйти из админки"
            >
              <SignOutIcon weight="bold" className="size-3.5" />
              Выйти
            </button>
          </div>
        </header>
        <main className="min-h-0 min-w-0 flex-1 overflow-auto p-5">{children}</main>
      </div>
    </div>
  );
}
